import { useCallback, useEffect, useRef, useState } from 'react';
import useLogActivityAccess from '../hooks/useLogActivityAccess';
import './ColorGame.css';

function playSound(src) {
  try { new Audio(src).play(); } catch {}
}

// ── Paleta completa ──────────────────────────────────────
const COLORS = {
  red: '#E53935', blue: '#1565C0', green: '#2E7D32',
  yellow: '#F9A825', purple: '#6A1B9A', orange: '#E65100',
  pink: '#E91E63', brown: '#4E342E', cyan: '#00838F',
  magenta: '#AD1457', lime: '#558B2F', teal: '#00695C',
  indigo: '#283593', gold: '#F57F17', silver: '#607D8B',
  gray: '#455A64', maroon: '#880E4F', navy: '#1A237E',
  coral: '#E64A19', turquoise: '#00796B', violet: '#7B1FA2',
  salmon: '#BF360C', khaki: '#827717', skyblue: '#0277BD',
  chocolate: '#5D4037', crimson: '#C62828', olive: '#558B00',
};

// ── Badges por nível ─────────────────────────────────────
const BADGES = [
  '', '🌟 Iniciante', '🎨 Intermediário', '🌈 Avançado',
  '🏆 Mestre', '🚀 Expert Neon', '🎮 Modo Inverso', '👑 Campeão',
];

// ── Requisitos por nível (acertos para subir) ────────────
const LEVEL_REQ = [0, 3, 5, 8, 12, 15, 20, 25];

const TIMER_INIT = 15;
const OPTIONS_COUNT = 4;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickOptions(correct, allKeys, count) {
  const others = shuffle(allKeys.filter(k => k !== correct));
  return shuffle([correct, ...others.slice(0, count - 1)]);
}

export default function ColorGame() {
  useLogActivityAccess('color-game');

  const [screen,    setScreen]    = useState('start');  // start | game | over | champion
  const [level,     setLevel]     = useState(1);
  const [score,     setScore]     = useState(0);
  const [combo,     setCombo]     = useState(0);
  const [timeLeft,  setTimeLeft]  = useState(TIMER_INIT);
  const [correct,   setCorrect]   = useState('');
  const [options,   setOptions]   = useState([]);
  const [answered,  setAnswered]  = useState(null);  // color name chosen or null
  const [result,    setResult]    = useState('');
  const [anim,      setAnim]      = useState('');    // 'shake' | 'bounce'
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [levelScore, setLevelScore]  = useState(0);  // score within this level
  const timerRef = useRef(null);

  const colorKeys  = Object.keys(COLORS);
  const isInverse  = level >= 6;  // modo inverso

  // ── Gerar nova pergunta ──────────────────────────────────
  const newQuestion = useCallback(() => {
    const correctColor = colorKeys[Math.floor(Math.random() * colorKeys.length)];
    const opts = pickOptions(correctColor, colorKeys, OPTIONS_COUNT);
    setCorrect(correctColor);
    setOptions(opts);
    setAnswered(null);
    setResult('');
    setTimeLeft(TIMER_INIT);
  }, [colorKeys]);

  // ── Start/reset ──────────────────────────────────────────
  function startGame() {
    setLevel(1);
    setScore(0);
    setCombo(0);
    setLevelScore(0);
    setScreen('game');
    // question triggered by useEffect below
  }

  useEffect(() => {
    if (screen === 'game') newQuestion();
  }, [screen]); // eslint-disable-line

  // ── Timer ────────────────────────────────────────────────
  useEffect(() => {
    if (screen !== 'game' || answered !== null) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleTimeout();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [correct, screen]); // eslint-disable-line

  function handleTimeout() {
    playSound('/sound/disappointed.mp3');
    setResult(`fail`);
    setAnim('shake');
    setCombo(0);
    setLevelScore(0);
    setLevel(1);
    setTimeout(() => { setAnim(''); newQuestion(); }, 1200);
  }

  // ── Check answer ─────────────────────────────────────────
  function checkAnswer(chosen) {
    if (answered !== null) return;
    clearInterval(timerRef.current);
    setAnswered(chosen);

    if (chosen === correct) {
      playSound('/sound/applause.mp3');
      setResult('ok');
      setAnim('bounce');
      const newCombo = combo + 1;
      const bonus = newCombo >= 3 ? 2 : 1;
      setCombo(newCombo);
      setScore(s => s + bonus);

      const newLevelScore = levelScore + 1;
      setLevelScore(newLevelScore);

      if (newLevelScore >= LEVEL_REQ[level]) {
        const nextLevel = level + 1;
        if (nextLevel > 7) {
          setTimeout(() => setScreen('champion'), 1400);
          return;
        }
        playSound('/sound/level-up.mp3');
        setLevel(nextLevel);
        setLevelScore(0);
        setShowLevelUp(true);
        setTimeout(() => {
          setShowLevelUp(false);
          setAnim('');
          newQuestion();
        }, 2200);
      } else {
        setTimeout(() => { setAnim(''); newQuestion(); }, 1100);
      }
    } else {
      playSound('/sound/disappointed.mp3');
      setResult('fail');
      setAnim('shake');
      setCombo(0);
      setLevelScore(0);
      setLevel(1);
      setTimeout(() => { setAnim(''); newQuestion(); }, 1400);
    }
  }

  // ── Render: start ────────────────────────────────────────
  if (screen === 'start') return (
    <div className="cg-page">
      <div className="cg-center">
        <div style={{ fontSize: '3.5rem', marginBottom: 8 }}>🎨</div>
        <h1>Color Name Game</h1>
        <p>Veja a cor e escolha o nome certo — ou o contrário no modo avançado!</p>

        <div className="cg-badge-list">
          {BADGES.slice(1).map((b, i) => (
            <span key={i} className="cg-badge-chip">{b}</span>
          ))}
        </div>

        <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', marginBottom: 24, fontWeight: 400 }}>
          Nível 6+: Modo inverso — veja o nome e clique na cor certa!
        </div>

        <button className="cg-btn cg-btn-primary" onClick={startGame}>
          <i className="fas fa-play" /> Start Game
        </button>
      </div>
    </div>
  );

  // ── Render: champion ─────────────────────────────────────
  if (screen === 'champion') return (
    <div className="cg-page">
      <div className="cg-center">
        <div style={{ fontSize: '4rem', marginBottom: 10 }}>👑</div>
        <h1>🎉 Você Venceu! 🎉</h1>
        <p>Parabéns! Você completou todos os {BADGES.length - 1} níveis!</p>
        <div style={{
          background: 'rgba(255,255,255,0.07)', borderRadius: 16, padding: '16px 24px',
          margin: '0 auto 24px', maxWidth: 200,
        }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: '#fbbf24' }}>{score}</div>
          <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>pontos totais</div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button className="cg-btn cg-btn-primary" onClick={startGame}>
            <i className="fas fa-redo" /> Jogar de novo
          </button>
        </div>
      </div>
    </div>
  );

  // ── Render: game ─────────────────────────────────────────
  return (
    <div className="cg-page">
      <div className="cg-card">

        {/* Status bar */}
        <div className="cg-statusbar">
          <span className="cg-badge-display">{BADGES[level] || BADGES[BADGES.length - 1]}</span>
          <div className="cg-info">
            <span className="cg-info-item score"><i className="fas fa-star" /> {score}</span>
            <span className="cg-info-item level"><i className="fas fa-layer-group" /> Lv.{level}</span>
            {combo >= 2 && <span className="cg-info-item combo"><i className="fas fa-fire" /> ×{combo}</span>}
          </div>
        </div>

        {/* Timer */}
        <div className="cg-timer-bar">
          <div
            className={`cg-timer-fill${timeLeft <= 5 ? ' urgent' : ''}`}
            style={{ width: `${(timeLeft / TIMER_INIT) * 100}%` }}
          />
        </div>
        <div className="cg-timer-label">{timeLeft}s</div>

        {/* Level up overlay */}
        {showLevelUp ? (
          <div className="cg-levelup-screen">
            <div style={{ fontSize: '3rem' }}>🎉</div>
            <h2>Nível {level}!</h2>
            <p>{BADGES[level]}</p>
            {isInverse && <p style={{ color: '#fbbf24', fontWeight: 700, marginTop: 8 }}>
              🎮 Modo Inverso ativado!
            </p>}
          </div>
        ) : (
          <>
            {/* Color/Word display */}
            <div
              className={`cg-color-box ${anim}`}
              onAnimationEnd={() => setAnim('')}
              style={isInverse ? { backgroundColor: 'transparent', border: '2px solid rgba(255,255,255,0.1)' } : { backgroundColor: COLORS[correct] }}
            >
              {isInverse && (
                <span className="cg-word-display"
                      style={{ color: COLORS[correct], textShadow: `0 0 20px ${COLORS[correct]}` }}>
                  {correct}
                </span>
              )}
            </div>

            {/* Question */}
            <div className="cg-question">
              {isInverse ? 'Click the matching color:' : 'What color is this?'}
            </div>

            {/* Options */}
            <div className="cg-options">
              {options.map(opt => {
                let cls = '';
                if (answered !== null) {
                  if (opt === correct) cls = 'correct';
                  else if (opt === answered && opt !== correct) cls = 'wrong';
                }
                return isInverse ? (
                  /* Inverse: color patches */
                  <button
                    key={opt}
                    className={`cg-option-patch ${cls}`}
                    style={{ backgroundColor: COLORS[opt], borderColor: cls === 'correct' ? '#34d399' : cls === 'wrong' ? '#f87171' : 'transparent' }}
                    onClick={() => checkAnswer(opt)}
                    disabled={answered !== null}
                    title={opt}
                  />
                ) : (
                  /* Normal: word buttons */
                  <button
                    key={opt}
                    className={`cg-option-word ${cls}`}
                    onClick={() => checkAnswer(opt)}
                    disabled={answered !== null}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>

            {/* Progress within level */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ flex: 1, height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', background: '#34d399',
                  width: `${(levelScore / (LEVEL_REQ[level] || 1)) * 100}%`,
                  transition: 'width 0.4s ease'
                }} />
              </div>
              <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                {levelScore}/{LEVEL_REQ[level]}
              </span>
            </div>

            {/* Result */}
            <div className={`cg-result${result ? ` ${result}` : ''}`}>
              {result === 'ok'   && `✓ Correct! ${combo >= 3 ? `🔥 Combo ×${combo}!` : ''}`}
              {result === 'fail' && (answered === 'timeout' ? '⏱ Time\'s up!' : `✗ It was "${correct}"!`)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
